import './custom.css';

import React from 'react';
import { Provider } from 'react-redux';
import { createBrowserHistory } from 'history';
import { ConnectedRouter } from 'connected-react-router';
import { ChakraProvider } from '@chakra-ui/react';
import { QueryClient } from '@tanstack/react-query';

import theme from 'constants/chakra-ui-theme';
import configureStore from 'store/configureStore';
import AxiosInterceptorUtils from 'common/helpers/axios-intercepter';

import Layout from './routes/Layout';
import UserProvider from './context/UserProvider';
import { LoaderProvider, UserApplicationProvider } from './common/contexts';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import {
    PersistQueryClientProvider,
    removeOldestQuery
} from '@tanstack/react-query-persist-client';
import F1Assistant from 'presentations/view/F1Assistant/F1Assistant';
import { useStore } from 'common/hooks';
import { observer } from 'mobx-react';

AxiosInterceptorUtils.initialInterceptRequest();

// Create browser history to use in the Redux store
const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href') as string;
const history = createBrowserHistory({ basename: baseUrl });

// Get the application-wide store instance, prepopulating with state from the server where available.
const store = configureStore(history);

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            gcTime: 1000 * 60 * 60 * 24 * 3
        }
    }
});

const localStoragePersister = createSyncStoragePersister({
    storage: window.localStorage,
    retry: removeOldestQuery
});

function App() {
const { subDashboardStore } = useStore();

    return (
        <ChakraProvider theme={theme}>
            <Provider store={store}>
                <ConnectedRouter history={history}>
                    <UserProvider>
                        <LoaderProvider>
                            <PersistQueryClientProvider
                                client={queryClient}
                                persistOptions={{ persister: localStoragePersister }}
                            >
                                <UserApplicationProvider>
                                    <Layout />

                                    {subDashboardStore.selectedApplication && <F1Assistant />}
                                </UserApplicationProvider>
                            </PersistQueryClientProvider>
                        </LoaderProvider>
                    </UserProvider>
                </ConnectedRouter>
            </Provider>
        </ChakraProvider>
    );
}

export default observer(App);
